import React from 'react';
import _ from 'lodash';

import List from '@mui/material/List';

import { useCurrentTracklog } from 'ff/debrief/hooks';
import { useGetAllTracklogsQuery } from 'ff/debrief/features/TracklogApi';
import { PAGE_SIZE, PAGE_HEIGHT } from 'ff/debrief/utils';
import TracklogListPage from './TracklogListPage';
import GhostItem from './GhostItem';
import RedirectToShared from 'ff/debrief/router/links/RedirectToShared';

interface Props {
    startTimestamp: number;
    endTimestamp: number;
    fuzzySearchTerm: string;
}

const TracklogList: React.FC<Props> = props => {
    const listRef = React.useRef<HTMLUListElement>(null);
    const q = useGetAllTracklogsQuery({
        page: 0,
        startTimestamp: props.startTimestamp,
        endTimestamp: props.endTimestamp,
        fuzzySearchTerm: props.fuzzySearchTerm,
    });
    const fullListQuery = useGetAllTracklogsQuery({
        page: 0,
    });
    // Need this to trigger auto select
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const t = useCurrentTracklog({ autoSelect: true });

    // Scroll to the page containing the selected tracklog
    React.useEffect(() => {
        if (!q.isLoading && listRef.current && t.currentPage !== undefined) {
            // Find the scrollable container by traversing up the DOM
            let element: HTMLElement | null = listRef.current.parentElement;
            while (element) {
                const overflowY = window.getComputedStyle(element).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    const scrollPosition = t.currentPage * PAGE_HEIGHT;
                    element.scrollTo({
                        top: scrollPosition,
                        behavior: 'instant',
                    });
                    break;
                }
                element = element.parentElement;
            }
        }
    }, [q.isLoading, t.currentPage]);

    const totalPages = React.useMemo(() => {
        const totalItems = q.data?.totalTracklogs;
        return totalItems && Math.ceil(totalItems / PAGE_SIZE);
    }, [q.data?.totalTracklogs]);

    const isEmptyList = fullListQuery.data?.totalTracklogs === 0;

    if (q.isLoading) {
        return (
            <List ref={listRef} sx={{ padding: '0 4px 4px' }}>
                {_.range(PAGE_SIZE).map(idx => (
                    <GhostItem key={idx} />
                ))}
            </List>
        );
    } else if (isEmptyList) {
        return <RedirectToShared />;
    } else {
        return (
            <List ref={listRef} sx={{ padding: '0 4px 4px' }} dense>
                {_.range(totalPages).map((__, idx) => (
                    <TracklogListPage
                        startTimestamp={props.startTimestamp}
                        endTimestamp={props.endTimestamp}
                        fuzzySearchTerm={props.fuzzySearchTerm}
                        key={idx}
                        page={idx}
                    />
                ))}
            </List>
        );
    }
};

export default TracklogList;
